import React, { useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { connectMarketWS, onMarketMessage } from "./ws/marketWs.js";
import { connectSignalWS, onSignalMessage } from "./ws/signalWsClient.js";
import { applyPriceMessage } from "./store/marketPrices.js";
import AppRoutes from "./AppRoutes";
import "./styles/globals.css";
import "./App.css";

/**
 * App — root component.
 *
 * Responsibilities kept here (and nowhere else):
 *   1. Bootstrap the Router so all children can useNavigate / useParams.
 *   2. Connect the market-data WebSocket once on mount.
 *   3. Connect the signal-feed WebSocket once on mount (auth-aware).
 *   4. Render the global ToastContainer.
 *   5. Delegate all routing to <AppRoutes>.
 *
 * Route definitions live in routeConfig.js (data) and
 * AppRoutes.jsx (rendering). Adding a new page is a one-line
 * addition to routeConfig — no changes needed here.
 */

/** Format a signal into a human-readable toast line. */
function formatSignalToast(signal) {
  const action = signal.action_type || 'SIGNAL';
  const symbol = signal.symbol || '—';
  const type   = signal.signal_type || '';
  const name   = signal.strategy_name || '';

  let price = '';
  if (signal.trigger_price != null) {
    price = ` at ₹${Number(signal.trigger_price).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  // e.g. "BUY RELIANCE at ₹2,500.00"  or  "SELL TCS at ₹3,100.50 — EXIT"
  let line = `${action} ${symbol}${price}`;
  if (type === 'EXIT') line += ' — EXIT';
  if (name) line += `\n${name}`;
  return line;
}

function App() {
  useEffect(() => {
    // ── Market-data WebSocket (existing, untouched) ─────────────────
    connectMarketWS();

    const unsubMarket = onMarketMessage((msg) => {
      if (msg?.type === 'PRICE') {
        applyPriceMessage(msg);
      }
    });

    // ── Signal-feed WebSocket (new — global push notifications) ─────
    // connectSignalWS() is auth-aware: it silently no-ops if the user
    // isn't logged in yet, and stops reconnecting on 4401 (session
    // expired).  See ws/signalWsClient.js for details.
    connectSignalWS();

    const unsubSignals = onSignalMessage((msg) => {
      if (msg?.type !== 'NEW_SIGNAL' || !msg?.data) return;

      const signal = msg.data;
      const isBuy  = signal.action_type === 'BUY';

      toast(formatSignalToast(signal), {
        icon:      isBuy ? '📈' : '📉',
        autoClose: 8000,
        style: {
          borderLeft: `4px solid ${isBuy ? '#22c55e' : '#ef4444'}`,
          whiteSpace: 'pre-line',          // respect the \n in the message
        },
      });
    });

    // ── Cleanup on unmount (page refresh / logout) ──────────────────
    return () => {
      unsubMarket();
      unsubSignals();
    };
  }, []);

  return (
    <Router>
      <ToastContainer
        position="bottom-right"
        newestOnTop
        limit={5}
      />
      <AppRoutes />
    </Router>
  );
}

export default App;
